import { useEffect, useRef, useState } from 'react';

import { Center, Skeleton, Spinner, useColorModeValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { t } from 'i18next';
import { isEmpty, isUndefined, throttle } from 'lodash';
import EasyVirtualized from 'react-easy-virtualized';
import { Trans } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useExploreTradingPhotoCards } from '@pocapan/shared/domains/tradingPhotoCards/ui/hooks/useExploreTradingPhotoCards';
import TradingPhotoCardFilterBottomDrawer from './TradingPhotoCardFilterBottomDrawer';
import PhotoCardFilterButton from './PhotoCardFilterButton';
import ArtistDropdown from './ArtistDropdown';
import MemberDropdown from './MemberDropdown';
import EmptyExploreTradingPhotoCard from './EmptyExploreTradingPhotoCard';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { getCountryFlagEmoji } from '@pocapan/shared/utils/countryFlagEmoji';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { Button } from '@peacecover/component';

export default function SalesTabPanelV2({ fabWrapperRef }) {
  return <ExploreSaleTab fabWrapperRef={fabWrapperRef} />;
}

const SaleCardSkeletons = () => {
  const startColor = useColorModeValue('separator.light.1', 'separator.dark.1');
  const endColor = useColorModeValue('separator.light.2', 'separator.dark.2');

  return (
    <div className="flex flex-col">
      {[...Array(5).keys()].map((key) => (
        <div key={key} className="flex items-center gap-[16px] px-[24px] py-[12px]">
          <Skeleton
            width="104px"
            height="104px"
            borderRadius="12px"
            flexShrink={0}
            startColor={startColor}
            endColor={endColor}
          />
          <div className="flex flex-col gap-[8px] flex-grow">
            <Skeleton
              height="18px"
              width="70%"
              borderRadius="6px"
              startColor={startColor}
              endColor={endColor}
            />
            <Skeleton
              height="18px"
              width="40%"
              borderRadius="6px"
              startColor={startColor}
              endColor={endColor}
            />
            <Skeleton
              height="16px"
              width="50%"
              borderRadius="6px"
              startColor={startColor}
              endColor={endColor}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const ExploreSaleTab = ({ fabWrapperRef }) => {
  const queryClient = useQueryClient();
  const prevArtistRef = useRef(null);

  const [selectedArtist, setSelectedArtist] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [onlyIdentityVerified, setOnlyIdentityVerified] = useState(false);
  const [onlySameCountry, setOnlySameCountry] = useState(false);
  const [selectedPhotoCardCategories, setSelectedPhotoCardCategories] = useState([]);

  const [isFilteringDrawerOpen, setIsFilteringDrawerOpen] = useState(false);
  const [paginatedTradingPhotoCards, setPaginatedTradingPhotoCards] = useState();

  const { data, isFetching, fetchNextPage, hasNextPage } = useExploreTradingPhotoCards({
    artistId: selectedArtist?.artistId,
    memberIds: selectedMembers.map((m) => m.memberId),
    eventIds: selectedEventIds,
    onlyIdentityVerified: onlyIdentityVerified,
    onlySameCountry,
    photoCardCategories: selectedPhotoCardCategories,
    category: 'sale',
    sortBy: 'recent'
  });

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    const receiveMessage = throttle((event) => {
      if (event.origin !== window.location.origin) return;
      if (isEmpty(event.data?.type)) return;

      if (event.data?.type === 'refresh-sales-trading-photo-cards') {
        queryClient.invalidateQueries('exploreTradingPhotoCards');
      }
    }, 1000);

    window.addEventListener('message', receiveMessage, false);
    return () => {
      window.removeEventListener('message', receiveMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  useEffect(() => {
    if (prevArtistRef.current?.artistId !== selectedArtist?.artistId) {
      setSelectedMembers([]);
      setSelectedEventIds([]);
      setSelectedPhotoCardCategories([]);
      setOnlyIdentityVerified(false);
      setOnlySameCountry(false);
      setPaginatedTradingPhotoCards(undefined);
    }
    prevArtistRef.current = selectedArtist;
  }, [selectedArtist]);

  useEffect(() => {
    if (!isEmpty(data)) {
      const tradingPhotoCards = data.pages?.flatMap((page) =>
        page.tradingPhotoCards.map((tradingPhotoCard) => tradingPhotoCard)
      );
      setPaginatedTradingPhotoCards(tradingPhotoCards);
    }
  }, [data]);

  const handleFilterSelected = (filteredObj) => {
    setIsFilteringDrawerOpen(false);
    if (!isEmpty(filteredObj)) {
      setOnlyIdentityVerified(filteredObj.identityVerified);
      setOnlySameCountry(Boolean(filteredObj.sameCountryOnly));
      setSelectedEventIds(filteredObj.eventIds);
      setSelectedPhotoCardCategories(filteredObj.photoCardCategories);

      queryClient.invalidateQueries(['exploreTradingPhotoCards']);
    }
  };

  const onOpenFilterDrawer = (isOpen) => {
    if (fabWrapperRef.current) {
      fabWrapperRef.current.style.visibility = isOpen ? 'hidden' : 'visible';
    }
    setIsFilteringDrawerOpen(isOpen);
  };

  return (
    <div>
      <div className="py-[16px] px-[24px]">
        <div className="flex items-center gap-[10px]">
          <ArtistDropdown onSelected={setSelectedArtist} />
          {selectedArtist && (
            <MemberDropdown
              members={selectedArtist.members}
              selectedMembers={selectedMembers}
              onSelectedMembers={setSelectedMembers}
            />
          )}
          <PhotoCardFilterButton
            selectedPhotoCardCategories={selectedPhotoCardCategories}
            selectedEventIds={selectedEventIds}
            onlyIdentityVerified={onlyIdentityVerified}
            onlySameCountry={onlySameCountry}
            onClick={() => onOpenFilterDrawer(true)}
          />
        </div>
      </div>
      <div>
        {isUndefined(paginatedTradingPhotoCards) ? (
          <SaleCardSkeletons />
        ) : isEmpty(paginatedTradingPhotoCards) ? (
          <EmptyExploreTradingPhotoCard />
        ) : (
          <div className="pb-[calc(56px+env(safe-area-inset-bottom))]">
            <EasyVirtualized
              useParentScrollElement={true}
              onLoadMore={loadMore}
              hasMore={hasNextPage}
              loader={
                <Center marginTop={'8px'}>
                  <Spinner
                    size="sm"
                    className="text-primary-light dark:text-primary-dark"
                    marginRight={'20px'}
                  />
                </Center>
              }
            >
              {paginatedTradingPhotoCards.map((card) => (
                <motion.div
                  key={card.tradingPhotoCardId}
                  initial={{ opacity: 0, y: -1 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -1 }}
                  transition={{ duration: 0.3 }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      openNewTab(
                        `/trading_photo_cards/${card.tradingPhotoCardId}?photoCardId=${card.photoCard.photoCardId}`
                      )
                    }
                    className="flex items-center gap-[16px] w-full px-[24px] py-[12px]"
                  >
                    {card.pictureUrls?.length > 0 ? (
                      <img
                        src={card.pictureUrls[0]}
                        alt=""
                        className="w-[104px] h-[104px] object-cover rounded-[12px]"
                      />
                    ) : (
                      <div className="w-[104px] h-[104px] rounded-[12px] bg-surface-lv2 flex items-center justify-center">
                        <img
                          src={card.photoCard.mediumThumbnailUrl || card.photoCard.pictureUrl}
                          alt=""
                          className="w-[48px] h-[76px] rounded-[5px] object-cover shadow-[0_0_9.494px_0_rgba(0,0,0,0.10)]"
                        />
                      </div>
                    )}
                    <div className="flex flex-col gap-[8px] items-start flex-grow">
                      <div className="text-[15px] font-semibold leading-[18px] text-text-main-lv2">
                        <Trans
                          i18nKey="photoCardDetailPage.title"
                          values={{
                            memberName: card.photoCard.memberName,
                            categoryName: card.photoCard.categoryName,
                            categoryEmoji: card.photoCard.categoryEmoji
                          }}
                        />
                      </div>
                      <div className="text-[15px] font-medium leading-[18px] text-text-primary">
                        {isEmpty(card.price)
                          ? t('tradingPhotoCardPage.offerPriceWithEmoji')
                          : (card.localizedPrice?.converted.price ?? card.price)}
                      </div>
                      <div className="flex flex-row items-center gap-[3px] ml-[-1px]">
                        <ProfileAvatar
                          thumbnailUrl={card.userProfile.thumbnailUrl}
                          svgWidthAndHeight="16px"
                          widthAndHeight="16px"
                        />
                        <div className="text-left flex items-center gap-[4px] min-w-0">
                          <span className="text-[13px] font-medium leading-[16px] text-text-main-lv3 max-w-[60px] truncate">
                            {card.userProfile.nickname}
                          </span>
                          <span className="text-[13px] leading-[16px] shrink-0" aria-hidden>
                            {getCountryFlagEmoji(card.country)}
                          </span>
                          <span className="text-[11px] font-normal text-text-sub-lv1 ml-[4px] whitespace-nowrap">
                            · {card.requestedAt}
                          </span>
                        </div>
                      </div>
                      {card.tradeMethod && (
                        <div className="px-[8px] py-[4px] bg-surface-lv2 rounded-[4px] text-[11px] font-semibold leading-[13px]">
                          {t(`TradeMethod.${card.tradeMethod}`)}
                        </div>
                      )}
                    </div>
                    {!card.isMine && (
                      <Button
                        size="small"
                        btnColor="secondary"
                        className="px-[12px] text-[13px] font-medium whitespace-nowrap"
                      >
                        {t('TradingPhotoCardsByPhotoCardBottomDrawer.purchase')}
                      </Button>
                    )}
                  </button>
                </motion.div>
              ))}
            </EasyVirtualized>
          </div>
        )}
      </div>
      {isFilteringDrawerOpen && (
        <TradingPhotoCardFilterBottomDrawer
          isOpen={isFilteringDrawerOpen}
          onClose={() => onOpenFilterDrawer(false)}
          onConfirm={(filteredObj) => handleFilterSelected(filteredObj)}
          identityVerified={onlyIdentityVerified}
          sameCountryOnly={onlySameCountry}
          artistId={selectedArtist?.artistId}
          eventIds={selectedEventIds}
          photoCardCategories={selectedPhotoCardCategories}
        />
      )}
    </div>
  );
};
